import type { ReactNode } from 'react';

import {
  useAddToCartMutation,
  useQuantityInCart,
  useChangeCartQuantityMutation,
} from '@site-builder/block-helpers';

import { StoreCardContext } from './StoreCardContext';
import { getAvailabilityInfo } from '../../utils/limit';

import type { AnyItem } from '@site-builder/block-utils';

interface StoreCardWrapperProps {
  readonly item: AnyItem;
  readonly onPurchase?: () => void;
  readonly children?: ReactNode;
}

export const StoreCardWrapper = ({
  item,
  onPurchase,
  children,
}: StoreCardWrapperProps) => {
  const addToCart = useAddToCartMutation();
  const changeQuantityInCart = useChangeCartQuantityMutation();
  const quantityInCart = useQuantityInCart(item);

  const { hasLimit, availableAmount, maxAvailableAmount } = getAvailabilityInfo(
    item?.limits,
  );

  const contextValue = {
    hasLimit,
    item,
    quantityInCart,
    availableAmount,
    maxAvailableAmount,
    onPurchase,
    onAddToCart: () => {
      if (hasLimit && quantityInCart > 0) return;
      addToCart.mutate(item);
    },
    onInCartIncrement: () => {
      if (hasLimit && quantityInCart >= availableAmount) return;
      changeQuantityInCart.mutate({ item, quantity: 'increment' });
    },
    onInCartDecrement: () =>
      changeQuantityInCart.mutate({ item, quantity: 'decrement' }),
  };

  return (
    <StoreCardContext.Provider value={contextValue}>
      {children}
    </StoreCardContext.Provider>
  );
};
